import React, { useContext, useState } from 'react';
import Paper from '@mui/material/Paper';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {get, update, deleteOne, loginAs} from "./api";
import Button from "@mui/material/Button";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import Loading from "../molcules/Loading/Loading";
import MyTable from "../atoms/MyTable";
import AcceptDialog from 'molcules/Dialogs/AcceptDialog';
import DeleteDialog from 'molcules/Dialogs/DeleteDialog';
import { sendInvite } from 'Services/api';
import StaffForm from './StaffForm';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import {store} from '../store';
import {startLoginAs} from './loginAsSession';
import {getRoleLabel} from '../helpers/constants/roles';

const columns = [
  { title: 'Email', align:'left', field: 'email'},
  { title: 'Name', align:'right', field: 'name'},
  { title: 'Permission', align:'right', field: 'role', format: getRoleLabel},
  { title: 'Sites', align:'right', field: 'sites'},
  { title: 'Account Suspended', align:'right', field: 'suspended'}
];

export default function UsersList({
  accountType = 'app',
  heading = 'App Accounts',
  roleFilter = {role: {$ne: 'guest'}},
  allowCreate = true,
  allowInvite = true
}) {
  const queryClient = useQueryClient();
  const {state} = useContext(store);
  const canLoginAs = state?.user?.role === 'admin' && !state.user.impersonatedBy && accountType === 'app';
  const [loggingInAs, setLoggingInAs] = useState(false);
  const [loginAsError, setLoginAsError] = useState('');
  const [onInvite, setOnInvite] = useState(()=>{});
  const [showAcceptInvite, setShowAcceptInvite] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const {isLoading = true, data, error} = useQuery({
    queryKey: ['users', accountType],
    queryFn: () => get(roleFilter)
  });
  let users = data ? data.data : [];
  users = users.map(user => ({...user, name: `${user.firstName} ${user.lastName}`, suspended: user.suspendAccount ? 'Yes' : 'No'}));
  const userCount = data?.total ?? users.length;

  const rowClick = (event, row) => {
    setSelectedUser(row);
    setShowEdit(true);
  };

  const handleFormClose = () => {
    setShowCreate(false);
    setShowEdit(false);
    setSelectedUser(null);
    queryClient.invalidateQueries(['users']);
  };

  if (error) return <p>An error has occurred: {error.message}</p>;

  return (
    <div>
      {loginAsError && <Alert severity="error" onClose={() => setLoginAsError('')}>{loginAsError}</Alert>}
      <AcceptDialog
        show={showAcceptInvite}
        action={'Invite'}
        onAccept={onInvite}
        onClose={() => setShowAcceptInvite(false)}
        heading={'Are you sure you want to send an invite?'}
        message={'This will send an email to the staff member allowing them to register.'}/>

      <div className='heading'><span className="heading-text">{heading} {!isLoading && `(${userCount})`}</span>
        {allowCreate && <Button
          variant="outlined"
          color="primary"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => setShowCreate(true)}
          className="head-action"
        >New</Button>}
      </div>

      {isLoading && <Loading/>}

      {!!users.length &&
        <Paper className={'paper'}>
          <MyTable
            title={''}
            columns={columns}
            actions={[
              ...(canLoginAs ? [{
                content: (<Button variant="outlined" size="small" disabled={loggingInAs}>Login As</Button>),
                id: 'login-as'
              }] : []),
              ...(allowInvite ? [{
                content: (<Button variant={'contained'} size={'small'}>Invite</Button>),
                id: 'invite'
              }] : []),
              {
                content: (<Button variant={'contained'} size={'small'} color={'error'}>Delete</Button>),
                id: 'delete'
              }
            ]}
            data={users}
            initialOrder={{orderBy:'sites', order:'asc'}}
            onRowClick={rowClick}
            onActionClick={(e, actionId, staff) => {
              e.stopPropagation();
              if (actionId === 'login-as' && canLoginAs && !loggingInAs) {
                setLoggingInAs(true);
                setLoginAsError('');
                loginAs(staff._id).then(response => startLoginAs(response.data)).catch(error => {
                  setLoginAsError(error.errorArray?.join(' ') || error.message || 'Unable to log in as this user.');
                  setLoggingInAs(false);
                });
              }
              if (actionId === 'invite') {
                setShowAcceptInvite(true);
                setOnInvite(() => () => {
                  sendInvite({email:staff.email, siteName: 'RH'});
                  update(staff._id, {inviteStatus:'Invite Sent'});
                });
              }
              if (actionId === 'delete') {
                setSelectedUser(staff);
                setShowDelete(true);
              }
            }}
          />
        </Paper>
      }

      {!isLoading && users.length === 0 &&
      <p>No {heading.toLowerCase()} found{allowCreate && <>, <Button onClick={() => setShowCreate(true)} color="primary">create one</Button></>}.</p>}

      {allowCreate && <StaffForm
        open={showCreate}
        create={true}
        mode="user"
        onClose={handleFormClose}
        onSuccess={setSuccessMessage}
      />}

      {selectedUser && <StaffForm
        open={showEdit}
        user={selectedUser}
        create={false}
        mode="user"
        onClose={handleFormClose}
        onSuccess={setSuccessMessage}
      />}

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSuccessMessage('')}
          sx={{width: '100%'}}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      <DeleteDialog
        heading={'Are you sure you want to delete this user?'}
        message={'This action cannot be undone. The user will be permanently removed.'}
        show={showDelete}
        onDelete={() => {
          if (selectedUser) {
            deleteOne(selectedUser._id).then(() => {
              queryClient.invalidateQueries(['users']);
              setShowDelete(false);
              setSelectedUser(null);
            });
          }
        }}
        onClose={() => {
          setShowDelete(false);
          setSelectedUser(null);
        }}
      />
    </div>
  );
}
